import {useState, useMemo} from 'react';
import {Layout, Table, Tabs, Select, DatePicker, Input} from "antd";
import Headerbar from "../../navBars/Headerbar";
import Sidebar from "../../navBars/Sidebar";
import {collection, getFirestore, orderBy, query, limit} from "firebase/firestore";
import Firebase from "../../Firebase/Firebase";
import {useCollection} from "react-firebase-hooks/firestore";
import {Tag} from "primereact/tag";
import moment from "moment";
import {ACTION_LABELS, ACTION_SEVERITY, AuditAction} from "../../utils/auditLog";

const db = getFirestore(Firebase);
const {Content} = Layout;
const {TabPane} = Tabs;
const {Option} = Select;
const {RangePicker} = DatePicker;

const SEV_COLOR: Record<string, string> = {
    info:    '#0284c7',
    success: '#059669',
    warning: '#d97706',
    danger:  '#dc2626',
};

const SYS_SEV_COLOR: Record<string, string> = {
    error:   '#dc2626',
    warning: '#d97706',
    info:    '#0284c7',
};

const Logs = () => {
    const [collapsed, setCollapsed]       = useState(false);
    const [actionFilter, setActionFilter] = useState<string>('all');
    const [adminFilter, setAdminFilter]   = useState('');
    const [dateRange, setDateRange]       = useState<[any, any] | null>(null);

    const [auditSnap] = useCollection(
        query(collection(db, 'adminAuditLog'), orderBy('timestamp', 'desc'), limit(500)),
        {snapshotListenOptions: {includeMetadataChanges: true}}
    );
    const [sysSnap] = useCollection(
        query(collection(db, 'systemLogs'), orderBy('timestamp', 'desc'), limit(200)),
        {snapshotListenOptions: {includeMetadataChanges: true}}
    );

    const auditLogs = useMemo(() =>
        (auditSnap?.docs ?? []).map(d => ({id: d.id, ...d.data()}))
    , [auditSnap]);

    const sysLogs = useMemo(() =>
        (sysSnap?.docs ?? []).map(d => ({id: d.id, ...d.data()}))
    , [sysSnap]);

    const admins = useMemo(() => {
        const map: Record<string, string> = {};
        auditLogs.forEach((l: any) => { if (l.adminEmail) map[l.adminEmail] = l.adminEmail; });
        return Object.keys(map);
    }, [auditLogs]);

    const filteredAudit = useMemo(() => {
        let rows = auditLogs as any[];
        if (actionFilter !== 'all') rows = rows.filter(r => r.action === actionFilter);
        if (adminFilter)            rows = rows.filter(r => r.adminEmail?.includes(adminFilter));
        if (dateRange?.[0])         rows = rows.filter(r => {
            const ts = r.timestamp?.toDate?.() ?? new Date(r.timestamp);
            return moment(ts).isBetween(dateRange![0], dateRange![1], undefined, '[]');
        });
        return rows;
    }, [auditLogs, actionFilter, adminFilter, dateRange]);

    const formatTs = (ts: any) => {
        if (!ts) return '—';
        const d = ts?.toDate ? ts.toDate() : new Date(ts);
        return moment(d).format('DD MMM YYYY HH:mm:ss');
    };

    const auditColumns = [
        {
            title: 'Timestamp',
            key: 'ts',
            width: 170,
            render: (r: any) => (
                <span style={{fontSize:12, color:'#555'}}>{formatTs(r.timestamp)}</span>
            ),
        },
        {
            title: 'Admin',
            key: 'admin',
            render: (r: any) => (
                <div>
                    <div style={{fontSize:12, fontWeight:600, color:'#222'}}>{r.adminEmail ?? '—'}</div>
                    <div style={{fontSize:11, color:'#aaa'}}>{r.adminUid?.slice(0, 8)}…</div>
                </div>
            ),
        },
        {
            title: 'Action',
            key: 'action',
            render: (r: any) => {
                const label = ACTION_LABELS[r.action as AuditAction] ?? r.action;
                const sev   = ACTION_SEVERITY[r.action as AuditAction] ?? 'info';
                return (
                    <span style={{
                        fontSize:11, fontWeight:600, padding:'2px 10px',
                        borderRadius:20, background: SEV_COLOR[sev] + '18',
                        color: SEV_COLOR[sev], border: `1px solid ${SEV_COLOR[sev]}44`,
                    }}>
                        {label}
                    </span>
                );
            },
        },
        {
            title: 'Target',
            key: 'target',
            render: (r: any) => (
                <div>
                    {r.targetName && <div style={{fontSize:12, fontWeight:500}}>{r.targetName}</div>}
                    {r.targetType && <div style={{fontSize:11, color:'#888'}}>{r.targetType}</div>}
                    {r.targetId   && <div style={{fontSize:11, color:'#aaa'}}>{r.targetId?.slice(0,16)}…</div>}
                </div>
            ),
        },
        {
            title: 'Detail',
            key: 'detail',
            render: (r: any) => (
                <div style={{fontSize:12, color:'#555'}}>
                    {r.from && r.to && <span><b>{r.from}</b> → <b>{r.to}</b></span>}
                    {r.note && <span style={{color:'#888'}}>{r.note}</span>}
                </div>
            ),
        },
    ];

    const sysColumns = [
        {
            title: 'Timestamp',
            key: 'ts',
            width: 170,
            render: (r: any) => <span style={{fontSize:12, color:'#555'}}>{formatTs(r.timestamp)}</span>,
        },
        {
            title: 'Severity',
            key: 'level',
            render: (r: any) => {
                const level = r.level ?? r.severity ?? 'error';
                return (
                    <span style={{
                        fontSize:11, fontWeight:600, padding:'2px 10px',
                        borderRadius:20, background: SYS_SEV_COLOR[level] + '18',
                        color: SYS_SEV_COLOR[level], border: `1px solid ${SYS_SEV_COLOR[level]}44`,
                    }}>
                        {level.toUpperCase()}
                    </span>
                );
            },
        },
        {
            title: 'Function / Source',
            key: 'source',
            render: (r: any) => <span style={{fontSize:12, fontWeight:500}}>{r.source ?? r.function ?? '—'}</span>,
        },
        {
            title: 'Message',
            key: 'msg',
            render: (r: any) => (
                <div style={{fontSize:12, color:'#555', maxWidth:400,
                             overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap'}}>
                    {r.message ?? r.error ?? '—'}
                </div>
            ),
        },
        {
            title: 'Context',
            key: 'ctx',
            render: (r: any) => r.context ? (
                <span style={{fontSize:11, color:'#aaa'}}>{JSON.stringify(r.context).slice(0, 80)}</span>
            ) : null,
        },
    ];

    const actionOptions = [
        {value: 'all', label: 'All Actions'},
        ...Object.entries(ACTION_LABELS).map(([k, v]) => ({value: k, label: v})),
    ];

    return (
        <Layout style={{minHeight:'100vh'}}>
            <Headerbar collapsed={collapsed} setCollapsed={setCollapsed}/>
            <Layout className="site-layout">
                <Sidebar collapsed={collapsed}/>
                <Content>
                    <div style={{padding:'20px 24px'}}>
                        <div style={{marginBottom:20}}>
                            <h4 style={{fontWeight:700, fontSize:20, color:'#1a1a2e', margin:0}}>Logs</h4>
                            <span style={{fontSize:12, color:'#888'}}>Admin audit trail and system events</span>
                        </div>

                        <Tabs defaultActiveKey="audit" size="large">

                            {/* ── Admin Audit Trail ── */}
                            <TabPane
                                tab={<span>Admin Audit Trail <span style={{fontSize:11, color:'#888'}}>({filteredAudit.length})</span></span>}
                                key="audit">
                                <div style={{display:'flex', gap:12, marginBottom:16, flexWrap:'wrap'}}>
                                    <Select
                                        value={actionFilter}
                                        onChange={setActionFilter}
                                        style={{width:220}}
                                        placeholder="Filter by action"
                                        showSearch
                                        optionFilterProp="label"
                                        options={actionOptions}
                                    />
                                    <Input
                                        placeholder="Filter by admin email"
                                        value={adminFilter}
                                        onChange={e => setAdminFilter(e.target.value)}
                                        style={{width:220}}
                                        allowClear
                                    />
                                    <RangePicker
                                        onChange={val => setDateRange(val as any)}
                                        style={{width:260}}
                                    />
                                    <div style={{fontSize:12, color:'#888', alignSelf:'center'}}>
                                        Showing {filteredAudit.length} of {auditLogs.length} entries
                                        (last 500 loaded)
                                    </div>
                                </div>
                                <Table
                                    dataSource={filteredAudit}
                                    columns={auditColumns}
                                    rowKey="id"
                                    size="small"
                                    pagination={{pageSize:20, showSizeChanger:true}}
                                    scroll={{x:900}}
                                />
                            </TabPane>

                            {/* ── System Events ── */}
                            <TabPane
                                tab={<span>System Events <span style={{fontSize:11, color:'#888'}}>({sysLogs.length})</span></span>}
                                key="system">
                                {sysLogs.length === 0 ? (
                                    <div style={{padding:40, textAlign:'center', color:'#aaa'}}>
                                        <div style={{fontSize:32, marginBottom:8}}>✅</div>
                                        <p style={{fontWeight:600, color:'#555'}}>No system errors logged</p>
                                        <p style={{fontSize:13}}>
                                            Cloud Function failures and payment errors will appear here automatically.
                                        </p>
                                    </div>
                                ) : (
                                    <Table
                                        dataSource={sysLogs}
                                        columns={sysColumns}
                                        rowKey="id"
                                        size="small"
                                        pagination={{pageSize:20, showSizeChanger:true}}
                                        scroll={{x:900}}
                                        rowClassName={(r: any) =>
                                            (r.level ?? r.severity) === 'error' ? 'ant-table-row-danger' : ''
                                        }
                                    />
                                )}
                            </TabPane>

                        </Tabs>
                    </div>
                </Content>
            </Layout>
        </Layout>
    );
};

export default Logs;
